/**
 * @file PipelineList.tsx
 * @description Компактный список воронок сделок (phase19.31, разбор A:
 * «Воронки сделок» — строка 64): название · «по умолчанию» меткой · вид и
 * число стадий второй линией · «›». Вся строка — переход к стадиям воронки,
 * «⋯» справа — «Изменить» (право настройки CRM).
 *
 * Описание воронки показывается второй линией, только если это текст
 * магазина: служебные описания стартовых данных («Pre-sales… → WON… Order»)
 * скрыты (`pipelineDescription`).
 */

import { ChevronRight, Pencil } from "lucide-react";
import { Link } from "react-router";

import { Badge } from "../../ui/badge";
import { LIST_FRAME_CLASS } from "../../ui/ListStates";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { PIPELINE_KIND_SHORT, type PipelineDto } from "../../../lib/api-crm";
import { pipelineDescription, stagesCountText } from "../../../lib/crm-pipeline-view";
import { cn } from "@/lib/utils";

interface Props {
  pipelines: PipelineDto[];
  /** Адрес стадий воронки. */
  stagesHref: (pipeline: PipelineDto) => string;
  /** «Изменить» в «⋯»; не задано — меню нет (нет права настройки). */
  onEdit?: (pipeline: PipelineDto) => void;
}

const ROW_CLASS =
  "flex min-h-16 min-w-0 flex-1 items-center gap-3 px-4 py-2 outline-none transition-colors hover:bg-[var(--row-hover)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";

export function PipelineList({ pipelines, stagesHref, onEdit }: Props) {
  return (
    <ul aria-label="Воронки сделок" className={cn(LIST_FRAME_CLASS, "divide-y divide-[var(--border)]")}>
      {pipelines.map((p) => {
        const description = pipelineDescription(p.description);
        return (
          <li key={p.id} className="flex items-stretch">
            <Link to={stagesHref(p)} className={ROW_CLASS} aria-label={`Стадии воронки «${p.name}»`}>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="type-body truncate font-medium text-on-surface">{p.name}</span>
                  {p.isDefault ? <Badge variant="outline">по умолчанию</Badge> : null}
                  {!p.isActive ? <Badge variant="neutral">в архиве</Badge> : null}
                </span>
                <span className="type-caption truncate text-on-surface-variant">
                  {[PIPELINE_KIND_SHORT[p.kind], stagesCountText(p.stagesCount), description].filter(Boolean).join(" · ")}
                </span>
              </span>
              <ChevronRight aria-hidden className="size-4 shrink-0 text-on-surface-variant" />
            </Link>
            {onEdit ? (
              <div className="flex shrink-0 items-center pr-2">
                <RowActionsMenu
                  size="compact"
                  title={`Воронка «${p.name}»`}
                  ariaLabel={`Действия с воронкой «${p.name}»`}
                  actions={[{ key: "edit", label: "Изменить", icon: Pencil, run: () => onEdit(p) }]}
                />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
